import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useSearchParams } from 'react-router';

import { landingAfterVerify, requestEmailCode, verifyEmailCode } from '../../api/email-code';
import {
  CODE_SHAPE_ERROR,
  budgetLimitCopy,
  countdownLabel,
  emailCodeErrorCopy,
  isCodeShape,
  isSameAddress,
  nextCountdown,
  normalizeOtpInput,
  shouldAutoSubmitOtp,
  validateEmail,
} from '../../api/email-code-model';
import { ApiError, auth } from '../../api/client';
import { clearProviderMarker, writeProviderMarker } from '../../auth/session-provider';
import { Button, Field, Input } from '../../components/ui';
import './login.css';

/**
 * The sign-in screen, two providers side by side (wave 9, SR v2.1 §5.1).
 *
 *  «Войти через Яндекс» is the staff door and stays a FULL navigation: the OAuth
 *  flow is a server-side redirect chain, there is nothing to do in-page but hand
 *  the browser over. Error states come back as `?err=` flags on the way in.
 *
 *  «Вход по коду» is the curator door (contract #2.5/#2.6) and lives entirely in
 *  the page: address → «Получить код» → the 6-digit code → «Войти». Two rules the
 *  copy obeys:
 *    - NON-DISCLOSURE (A1): a wrong code, an expired code and an address no
 *      curator ever held are ONE sentence — «Неверный или истёкший код».
 *    - THE COOLDOWN IS ALWAYS DRAWABLE: the server ships `resendAfterSeconds` on
 *      the 200 (even for a suppressed send) and `Retry-After` on the 429, so the
 *      «Отправить код ещё раз» link counts down without an error on screen.
 *    - A LONG WAIT IS NAMED (Z1): when the wait is the address's hourly budget
 *      and not the routine cooldown, the screen says «Лимит попыток исчерпан…» —
 *      a silent hour-long countdown is what made the link read as dead.
 */

interface ErrorCopy {
  title: string;
  body: string;
  tone: 'warn' | 'info';
}

const DOMAINS = ['@ismart.org', '@razvitieonline.su'];

/** Pure so the unit tests can pin the copy against the `err` values. */
export function loginErrorCopy(err: string | null | undefined): ErrorCopy | null {
  switch (err) {
    case 'domain':
    case 'not_allowed':
      return {
        title: 'Этот аккаунт не допущен',
        body: `CB Manager работает только с аккаунтами ${DOMAINS.join(' и ')}. Войдите под рабочим аккаунтом.`,
        tone: 'warn',
      };
    case 'blocked':
      return {
        title: 'Доступ закрыт администратором',
        body: 'Этот аккаунт заблокирован в CB Manager. Если это ошибка — обратитесь к администратору системы.',
        tone: 'warn',
      };
    case 'session':
      return {
        title: 'Сессия закончилась',
        body: 'Войдите снова, чтобы продолжить с того места, где вы остановились.',
        tone: 'info',
      };
    case 'error':
      return {
        title: 'Не удалось войти',
        body: 'Вход не завершился. Попробуйте ещё раз, а если не повторится — обратитесь к администратору.',
        tone: 'warn',
      };
    default:
      return null;
  }
}

/** Stage of the e-mail branch: address first, code second. */
export type EmailCodeStage = 'email' | 'code';

export function LoginPage() {
  const [params] = useSearchParams();

  // The click starts a full page load: reflect it, so nobody clicks twice.
  const [redirecting, setRedirecting] = useState(false);

  // The e-mail branch's own state. `cooldown` counts down while > 0, and it
  // belongs to ONE address (`cooldownEmail`): the api's budget is per address, so
  // a cooldown for one mailbox must not silently block a corrected one — while
  // re-typing the SAME address must not be the loophole that burns the budget
  // through «Получить код» while the countdown is still running (the Z1 live log
  // shows exactly that: two requests 33s apart, both through the address stage).
  const [stage, setStage] = useState<EmailCodeStage>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [cooldownEmail, setCooldownEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const codeInputRef = useRef<HTMLInputElement>(null);

  const copy = loginErrorCopy(params.get('err'));
  // `?next=` is deliberately NOT appended to the login hop: the api has no
  // return-to parameter yet (docs/05 §4 #2 is the boilerplate flow), and an
  // unknown query key on an OAuth hop is noise, not signal.

  // A marker left by an earlier code login in this tab must not make the NEXT
  // session look like a cabinet session. This screen is where every session
  // starts, so this is where the marker dies; the verify writes a fresh one.
  useEffect(() => clearProviderMarker(window.sessionStorage), []);

  // The countdown ticks once a second until it runs out; the link re-enables then.
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((current) => nextCountdown(current)), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const sendCode = async () => {
    const shapeError = validateEmail(email);
    if (shapeError !== null) {
      setError(shapeError);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { resendAfterSeconds } = await requestEmailCode({ email });
      setCooldown(resendAfterSeconds);
      setCooldownEmail(email);
      setSent(true);
      setStage('code');
      // Z1 — a wait longer than the routine resend cooldown IS the budget: the
      // api ships the moment the address's oldest code of the hour falls out, and
      // the screen has to say so instead of drawing a silent endless countdown.
      setError(budgetLimitCopy(resendAfterSeconds));
      window.setTimeout(() => codeInputRef.current?.focus(), 0);
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.is('TOO_MANY_REQUESTS', 429)) {
        const seconds = requestError.metaNumber('retryAfterSeconds');
        // The 429 carries its own cooldown (`Retry-After` / `meta`), and the
        // countdown is drawn from it TOO — the wait is real whether or not the
        // request succeeded (SR §5.1: «отсчёт рисуется и без ошибки»). The copy
        // names the budget honestly when that wait is an hour, not a minute.
        if (seconds !== undefined) setCooldown(seconds);
        setError(budgetLimitCopy(seconds) ?? copyOf(requestError));
      } else {
        setError(copyOf(requestError));
      }
    } finally {
      setBusy(false);
    }
  };

  const signIn = async (override?: string) => {
    const candidate = override ?? code;
    if (!isCodeShape(candidate)) {
      setError(CODE_SHAPE_ERROR);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await verifyEmailCode({ email, code: candidate });
      // The session exists server-side now. `/auth/me` publishes the provider itself
      // (T.Y16); this marker stays as the fallback that covers the moment before that
      // answer is refetched (and an api pod older than the SPA, T.Y8's whole gap).
      writeProviderMarker(window.sessionStorage, 'EMAIL_CODE');
      // T.Z85 — the landing is the SESSION'S ROLE (the Z60 predicate), not the
      // provider: a КУРАТОР-role session opens «Кабинет», a staff session the
      // dashboard. The role is read off the `/auth/me` this hop answers.
      const landing = await landingAfterVerify();
      // A FULL load, not a client-side route: the app re-boots on a fresh
      // `/auth/me`, so no query can keep a pre-login 401 in its cache.
      window.location.assign(landing);
    } catch (requestError) {
      setError(copyOf(requestError));
      setBusy(false);
    }
  };

  return (
    <div className="interstitial">
      <div className="interstitial__card">
        <span className="interstitial__logo" aria-hidden="true">
          CB
        </span>
        <h1>CB Manager</h1>

        {copy ? (
          <div
            className={`login-error login-error--${copy.tone}`}
            role="alert"
            data-testid="login-error"
          >
            <p className="login-error__title">{copy.title}</p>
            <p className="login-error__body">{copy.body}</p>
          </div>
        ) : null}

        {/* Full navigation on purpose: see the comment above. `next` is not sent
            to the api — it cannot honour it yet — so nothing is appended. */}
        <a
          className="btn btn--primary"
          href={auth.loginUrl}
          data-testid="sign-in"
          aria-disabled={redirecting}
          onClick={() => setRedirecting(true)}
        >
          {redirecting ? 'Переходим…' : 'Войти через Яндекс'}
        </a>

        {redirecting ? (
          <p className="interstitial__hint" role="status" data-testid="sign-in-pending">
            Открываем страницу входа…
          </p>
        ) : null}

        <div className="login-divider" role="separator" aria-label="или">
          <span>или</span>
        </div>

        <EmailCodeBranch
          stage={stage}
          email={email}
          code={code}
          cooldown={cooldown}
          cooldownEmail={cooldownEmail}
          busy={busy}
          sent={sent}
          error={error}
          codeInputRef={codeInputRef}
          onEmailChange={setEmail}
          onCodeChange={setCode}
          onSendCode={() => void sendCode()}
          onSignIn={(value) => void signIn(value)}
          onBackToEmail={() => {
            setStage('email');
            setCode('');
            setSent(false);
            setError(null);
          }}
        />
      </div>
    </div>
  );
}

interface BranchProps {
  stage: EmailCodeStage;
  email: string;
  code: string;
  cooldown: number;
  /** The address the running cooldown belongs to — the budget is per address, so
   * the gate is too (Z1): another address stays sendable, the same one waits. */
  cooldownEmail: string;
  busy: boolean;
  sent: boolean;
  error: string | null;
  codeInputRef: RefObject<HTMLInputElement | null>;
  onEmailChange: (value: string) => void;
  onCodeChange: (value: string) => void;
  onSendCode: () => void;
  /** The argument is the code as the field now holds it — the autosubmit reads it
   * before the state update lands, so a stale value can never be verified. */
  onSignIn: (value?: string) => void;
  onBackToEmail: () => void;
}

function EmailCodeBranch(props: BranchProps) {
  const { stage, email, code, cooldown, cooldownEmail, busy, sent, error } = props;
  const waiting = cooldown > 0 && isSameAddress(email, cooldownEmail);
  const countdown = countdownLabel(cooldown);

  return (
    <div className="login-code" data-testid="email-code-branch">
      {stage === 'email' ? (
        <Field label="E-mail" htmlFor="login-email" hint="Код придёт на адрес, указанный вашей площадкой.">
          <Input
            id="login-email"
            data-testid="login-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            placeholder="name@example.ru"
            value={email}
            disabled={busy}
            onChange={(event) => props.onEmailChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') props.onSendCode();
            }}
          />
        </Field>
      ) : (
        <>
          <p className="login-code__sent" data-testid="login-code-sent" role="status">
            {sent
              ? `Код отправлен на ${email}. Проверьте почту — письмо приходит в течение пары минут.`
              : `Проверьте почту: код уходит на ${email}.`}
          </p>
          <Field label="Код из письма" htmlFor="login-code" hint="6 цифр. Код действует 10 минут.">
            {/* T.Y18 — the one-time-code field: digits only, autofill-friendly
                (`autocomplete="one-time-code"` + `inputmode="numeric"`), a wide
                monospace cell layout, and the sixth digit submits by itself. */}
            <Input
              id="login-code"
              ref={props.codeInputRef}
              data-testid="login-code"
              className="login-code__input"
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              value={code}
              disabled={busy}
              onChange={(event) => {
                const next = normalizeOtpInput(event.target.value);
                props.onCodeChange(next);
                if (shouldAutoSubmitOtp(next) && !busy) props.onSignIn(next);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') props.onSignIn();
              }}
            />
          </Field>
        </>
      )}

      {error !== null ? (
        <p className="field__error" role="alert" data-testid="login-code-error">
          {error}
        </p>
      ) : null}

      {stage === 'email' ? (
        <Button
          variant="secondary"
          data-testid="login-send-code"
          loading={busy}
          disabled={busy || waiting}
          title={countdown ?? undefined}
          onClick={props.onSendCode}
        >
          Получить код
        </Button>
      ) : (
        <div className="login-code__actions">
          <Button
            variant="primary"
            data-testid="login-verify"
            loading={busy}
            onClick={() => props.onSignIn()}
          >
            Войти
          </Button>
          <Button
            variant="ghost"
            size="sm"
            data-testid="login-resend"
            disabled={busy || waiting}
            title={countdown ?? undefined}
            onClick={props.onSendCode}
          >
            Отправить код ещё раз
          </Button>
          <Button variant="ghost" size="sm" data-testid="login-back" disabled={busy} onClick={props.onBackToEmail}>
            Другой адрес
          </Button>
        </div>
      )}

      {countdown !== null ? (
        <p className="login-code__cooldown" role="status" data-testid="login-cooldown">
          {countdown}
        </p>
      ) : null}
    </div>
  );
}

/** The one sentence a failed hop says (A1 — the api's message is never shown raw). */
function copyOf(error: unknown): string {
  if (error instanceof ApiError) return emailCodeErrorCopy(error.code, error.status);
  return emailCodeErrorCopy(undefined, 0);
}
